import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Store, Camera, Loader2, ClipboardList, Download } from "lucide-react";
import { fmtHHMM, agencyDate, agencyOffsetMinutes, AGENCY_TZ } from "@/lib/agencyTime";

// MDT DAR / BUSINESS CHECK panel — officers log activity when NOT on a call:
// business checks (optional manager contact), on-scene entries, photos.
export const DarPanel = ({ t, myUnit }) => {
  const [cats, setCats] = useState([]);
  const [quickNotes, setQuickNotes] = useState([]);
  const [savedLocs, setSavedLocs] = useState([]);
  const [showSaveLoc, setShowSaveLoc] = useState(false);
  const [locForm, setLocForm] = useState({ name: "", address: "", parent: "" });
  const [entries, setEntries] = useState([]);
  const [kind, setKind] = useState("business_check");
  const [day, setDay] = useState(agencyDate());
  const [allMine, setAllMine] = useState([]);
  const [showDays, setShowDays] = useState(false);
  const [f, setF] = useState({ location: "", manager: "", notes: "", category: "BUSINESS CHECK" });
  const [busy, setBusy] = useState(false);
  const [photoFor, setPhotoFor] = useState(null);
  const fileRef = useRef();
  // Officers can review + download up to the last 14 days of their checks (agency Eastern days).
  const dayList = [...Array(14)].map((_, i) =>
    new Date(Date.now() - i * 86400000).toLocaleDateString("en-CA", { timeZone: AGENCY_TZ }));
  const dayLabel = (d, i) => {
    const nice = new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
    return i === 0 ? `TODAY — ${nice}` : i === 1 ? `YESTERDAY — ${nice}` : nice.toUpperCase();
  };
  // Eastern calendar day an entry belongs to (naive ISO strings are UTC).
  const entryDay = (ts) => {
    let iso = ts;
    if (typeof iso === "string" && !/[Zz]|[+-]\d{2}:\d{2}$/.test(iso)) iso += "Z";
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: AGENCY_TZ });
  };
  const countByDay = allMine.reduce((acc, e) => { const d = entryDay(e.ts); acc[d] = (acc[d] || 0) + 1; return acc; }, {});
  const load = () => client.get("/dar", { params: { mine: true, date: day, tz_offset: agencyOffsetMinutes() } }).then((r) => setEntries(r.data)).catch(() => {});
  const loadAll = () => client.get("/dar", { params: { mine: true } }).then((r) => setAllMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); client.get("/dar/meta").then((r) => { setCats(r.data.categories); setQuickNotes(r.data.quick_notes || []); }); loadLocs(); }, []);
  const loadLocs = () => client.get("/dar/locations").then((r) => setSavedLocs(r.data)).catch(() => {});
  const saveLoc = async () => {
    if (!locForm.name.trim()) { toast.error("Location name required"); return; }
    try {
      await client.post("/dar/locations", locForm);
      toast.success(`“${locForm.name}” saved${locForm.parent ? ` under ${locForm.parent}` : ""}`);
      setLocForm({ name: "", address: "", parent: "" }); setShowSaveLoc(false); loadLocs();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const groupedLocs = savedLocs.reduce((acc, l) => {
    const p = l.parent || "OTHER / STANDALONE";
    (acc[p] = acc[p] || []).push(l);
    return acc;
  }, {});

  const downloadPdf = async (path, params, fname, okMsg) => {
    if (!myUnit) { toast.error("Sign on with your unit callsign first"); return; }
    try {
      const r = await client.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(okMsg);
    } catch { toast.error("Download failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await client.post("/dar", { kind, unit: myUnit, location: f.location, manager: f.manager, notes: f.notes,
        category: kind === "business_check" ? "BUSINESS CHECK" : f.category });
      toast.success(kind === "business_check" ? "Business check logged" : "DAR entry logged");
      setF({ location: "", manager: "", notes: "", category: f.category });
      load(); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Entry failed"); }
    setBusy(false);
  };
  const uploadPhoto = async (files) => {
    if (!files?.length || !photoFor) return;
    try {
      const fd = new FormData();
      [...files].forEach((x) => fd.append("files", x));
      await client.post(`/dar/${photoFor}/photos`, fd);
      toast.success("Photo attached"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Photo upload failed"); }
    setPhotoFor(null);
  };
  const inp = "w-full border px-2 py-2 text-sm font-mono outline-none rounded";
  const istyle = { background: t.bg, borderColor: t.border, color: t.text };
  return (
    <div className="flex-1 overflow-y-auto rounded-lg border p-3 space-y-3" style={{ background: t.panel, borderColor: t.border }} data-testid="mdt-dar-view">
      <div className="flex gap-2">
        <button data-testid="dar-kind-check" onClick={() => setKind("business_check")}
          className="flex-1 py-2 text-xs font-head font-bold rounded flex items-center justify-center gap-2"
          style={kind === "business_check" ? { background: "#1d5d90", color: "#fff" } : { background: t.bg, color: t.dim, border: `1px solid ${t.border}` }}>
          <Store size={13} /> BUSINESS CHECK
        </button>
        <button data-testid="dar-kind-dar" onClick={() => setKind("dar")}
          className="flex-1 py-2 text-xs font-head font-bold rounded flex items-center justify-center gap-2"
          style={kind === "dar" ? { background: "#1d5d90", color: "#fff" } : { background: t.bg, color: t.dim, border: `1px solid ${t.border}` }}>
          <ClipboardList size={13} /> DAR ENTRY
        </button>
      </div>
      {kind === "dar" && (
        <select data-testid="dar-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp} style={istyle}>
          {cats.filter((c) => c !== "BUSINESS CHECK").map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      {savedLocs.length > 0 && (
        <select data-testid="dar-saved-loc" value=""
          onChange={(e) => {
            const l = savedLocs.find((x) => x.id === e.target.value);
            if (l) setF({ ...f, location: `${l.name}${l.parent ? ` (${l.parent})` : ""}${l.address ? ` — ${l.address}` : ""}` });
          }} className={inp} style={istyle}>
          <option value="">SAVED LOCATIONS — one-tap fill…</option>
          {Object.entries(groupedLocs).map(([parent, locs]) => (
            <optgroup key={parent} label={parent.toUpperCase()}>
              {locs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ""}</option>)}
            </optgroup>
          ))}
        </select>
      )}
      <input data-testid="dar-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}
        placeholder={kind === "business_check" ? "Business name / location *" : "Location (optional)"} className={inp} style={istyle} />
      <button data-testid="dar-saveloc-toggle" onClick={() => setShowSaveLoc(!showSaveLoc)}
        className="text-[10px] font-head font-bold" style={{ color: "#67e8f9" }}>
        {showSaveLoc ? "− CANCEL" : "+ SAVE A LOCATION (group under a parent complex)"}
      </button>
      {showSaveLoc && (
        <div className="rounded border p-2 space-y-1.5" style={{ background: t.bg, borderColor: "#1d5d90" }} data-testid="dar-saveloc-form">
          <input data-testid="dar-loc-name" value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })}
            placeholder="Business name * (e.g. ALDI)" className={inp} style={istyle} />
          <input data-testid="dar-loc-address" value={locForm.address} onChange={(e) => setLocForm({ ...locForm, address: e.target.value })}
            placeholder="Address (e.g. 8712 Liberty Rd)" className={inp} style={istyle} />
          <input data-testid="dar-loc-parent" value={locForm.parent} onChange={(e) => setLocForm({ ...locForm, parent: e.target.value })}
            placeholder="Parent complex (e.g. Liberty Road Shopping Center)" list="dar-parents" className={inp} style={istyle} />
          <datalist id="dar-parents">
            {[...new Set(savedLocs.map((l) => l.parent).filter(Boolean))].map((p) => <option key={p} value={p} />)}
          </datalist>
          <button data-testid="dar-loc-save" onClick={saveLoc} className="w-full py-1.5 text-xs font-head font-bold text-white rounded" style={{ background: "#1d5d90" }}>SAVE LOCATION</button>
        </div>
      )}
      <input data-testid="dar-manager" value={f.manager} onChange={(e) => setF({ ...f, manager: e.target.value })}
        placeholder="Manager / contact spoken with (optional)" className={inp} style={istyle} />
      <select data-testid="dar-quick-note" value=""
        onChange={(e) => { if (e.target.value) setF({ ...f, notes: f.notes ? `${f.notes}\n${e.target.value}` : e.target.value }); }}
        className={inp} style={istyle}>
        <option value="">QUICK NOTES — select to pre-fill (editable)…</option>
        {quickNotes.map((n, i) => <option key={i} value={n}>{n}</option>)}
      </select>
      <textarea data-testid="dar-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3}
        placeholder="Notes (e.g. all secure, spoke with staff, EMS on scene)" className={inp} style={istyle} />
      <button data-testid="dar-submit" onClick={submit} disabled={busy}
        className="w-full py-2.5 text-sm font-head font-bold text-white rounded disabled:opacity-50" style={{ background: "#16A34A" }}>
        {busy ? "LOGGING…" : kind === "business_check" ? "LOG BUSINESS CHECK" : "LOG DAR ENTRY"}
      </button>

      <div className="font-head text-xs font-bold pt-1 flex items-center gap-2 flex-wrap" style={{ color: t.dim }}>
        <span className="shrink-0">MY CHECKS — UNIT {myUnit || "—"}</span>
        <select data-testid="dar-day-select" value={day} onChange={(e) => setDay(e.target.value)}
          className="border px-2 py-1.5 text-[10px] font-mono outline-none rounded"
          style={{ background: t.bg, borderColor: t.border, color: t.text }}>
          {dayList.map((d, i) => <option key={d} value={d}>{dayLabel(d, i)}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button data-testid="dar-download-day" onClick={() => downloadPdf(
            "/records/daily-activity.pdf", { unit: myUnit, date: day, tz_offset: agencyOffsetMinutes() },
            `my_checks_${myUnit}_${day}.pdf`, `Checks report for ${day} downloaded`)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold text-white rounded" style={{ background: "#1d5d90" }}>
            <Download size={11} /> {day === agencyDate() ? "DOWNLOAD MY DAY (PDF)" : "DOWNLOAD THIS DAY (PDF)"}
          </button>
          <button data-testid="dar-download-7day" onClick={() => downloadPdf(
            "/records/activity-range.pdf", { unit: myUnit, days: 7, end_date: agencyDate(), tz_offset: agencyOffsetMinutes() },
            `my_checks_${myUnit}_last7days.pdf`, "7-day combined report downloaded")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold rounded border"
            style={{ borderColor: "#1d5d90", color: "#67e8f9", background: t.bg }}>
            <Download size={11} /> 7-DAY PDF
          </button>
          <button data-testid="dar-download-14day" onClick={() => downloadPdf(
            "/records/activity-range.pdf", { unit: myUnit, days: 14, end_date: agencyDate(), tz_offset: agencyOffsetMinutes() },
            `my_checks_${myUnit}_last14days.pdf`, "14-day combined report downloaded")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-head font-bold rounded border"
            style={{ borderColor: "#1d5d90", color: "#67e8f9", background: t.bg }}>
            <Download size={11} /> 14-DAY PDF
          </button>
        </div>
      </div>
      {entries.length === 0 && <div className="font-mono text-[11px]" style={{ color: t.dim }}>No entries logged {day === agencyDate() ? "today" : `on ${day}`}.</div>}
      {entries.map((e) => (
        <div key={e.id} className="rounded border p-2.5" style={{ background: t.bg, borderColor: t.border }} data-testid={`dar-entry-${e.id}`}>
          <div className="flex items-center gap-2 font-mono text-[11px] font-bold" style={{ color: "#67e8f9" }}>
            {fmtHHMM(e.ts)} HRS · {e.category}
            <span className="ml-auto font-normal" style={{ color: t.dim }}>{(e.photos || []).length > 0 && `${e.photos.length} photo(s)`}</span>
          </div>
          {e.location && <div className="text-sm font-head" style={{ color: t.text }}>{e.location}</div>}
          {e.manager && <div className="font-mono text-[10px]" style={{ color: t.dim }}>SPOKE WITH: {e.manager}</div>}
          {e.notes && <div className="text-[12px] mt-0.5" style={{ color: t.text }}>{e.notes}</div>}
          <button data-testid={`dar-photo-${e.id}`} onClick={() => { setPhotoFor(e.id); fileRef.current?.click(); }}
            className="mt-1.5 flex items-center gap-1.5 px-2 py-1 text-[10px] font-head font-bold rounded text-white" style={{ background: "#1d5d90" }}>
            <Camera size={11} /> ADD PHOTO
          </button>
        </div>
      ))}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
        data-testid="dar-photo-input" onChange={(ev) => uploadPhoto(ev.target.files)} />

      {/* DAY-BY-DAY DOWNLOADS — every one of the last 14 days, each with its own PDF */}
      <div className="rounded border" style={{ background: t.bg, borderColor: t.border }} data-testid="dar-daybyday">
        <button data-testid="dar-daybyday-toggle" onClick={() => setShowDays(!showDays)}
          className="w-full px-3 py-2 text-left font-head text-[11px] font-bold flex items-center gap-2"
          style={{ color: "#67e8f9" }}>
          <Download size={12} /> DAY-BY-DAY DOWNLOADS — LAST 14 DAYS {showDays ? "▾" : "▸"}
        </button>
        {showDays && (
          <div className="divide-y" style={{ borderColor: t.border }}>
            {dayList.map((d, i) => (
              <div key={d} className="px-3 py-1.5 flex items-center gap-2" style={{ borderColor: t.border }}>
                <button data-testid={`dar-day-view-${d}`} onClick={() => setDay(d)}
                  className="flex-1 text-left font-mono text-[11px] hover:underline"
                  style={{ color: day === d ? "#67e8f9" : t.text }}>
                  {dayLabel(d, i)}
                </button>
                <span className="font-mono text-[10px] shrink-0" style={{ color: countByDay[d] ? "#4ade80" : t.dim }}>
                  {countByDay[d] || 0} check{(countByDay[d] || 0) === 1 ? "" : "s"}
                </span>
                <button data-testid={`dar-day-dl-${d}`} title={`Download ${d} PDF`}
                  onClick={() => downloadPdf("/records/daily-activity.pdf",
                    { unit: myUnit, date: d, tz_offset: agencyOffsetMinutes() },
                    `my_checks_${myUnit}_${d}.pdf`, `Checks report for ${d} downloaded`)}
                  className="shrink-0 flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold text-white rounded"
                  style={{ background: "#1d5d90" }}>
                  <Download size={10} /> PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
